import { useId, useState } from 'react';
import { useWorkflowScope, workflowJson } from '../pages/mock/ContentApproval';
import { Button } from './ui';
import { ModalShell } from './menu';

// 모달 껍데기(role=dialog·포커스 트랩·Esc·opener 복귀)와 header/body/footer 3분할은 ModalShell이
// 맡는다(2026-09-16 검수 P2·S3). 여기서 따로 dialog 래퍼를 두지 않는다 — 중첩 dialog가 된다.
function ClaimForm({ initialCode, onClose, onClaimed }: { initialCode: string; onClose: () => void; onClaimed: () => void }): JSX.Element {
  const work = useWorkflowScope();
  const [code, setCode] = useState(initialCode);
  const [name, setName] = useState('새 디스플레이');
  const [groupId, setGroupId] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const ids = useId();
  const scope = work.scope;
  const writer = !!scope && ['Owner', 'Admin'].includes(scope.actor.role ?? '');
  const groups = scope?.groups.filter(group => writer && (!scope.actor.enforced || scope.actor.central ||
    scope.actor.groups.some(grant => grant.groupId === group.id && ['Owner', 'Admin'].includes(grant.role)))) ?? [];
  const selected = groups.some(group => group.id === groupId) ? groupId : groups[0]?.id ?? '';
  const cleanCode = code.toUpperCase().replace(/[\s-]/g, '');
  const busy = work.loading || work.pending;
  const valid = /^[ABCDEFGHJKLMNPQRSTUVWXYZ23456789]{10}$/.test(cleanCode) && name.trim().length > 0 && name.trim().length <= 100 && !!selected;
  const submit = async () => {
    if (!writer || !valid || !confirmed || !work.begin()) return;
    setError(null);
    try {
      await workflowJson('devices/pairing/claim', { method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: cleanCode, name: name.trim(), groupId: selected }) });
      onClaimed(); onClose();
    } catch (reason) { setError(reason instanceof Error ? reason.message : String(reason)); }
    finally { work.finish(); }
  };
  return <ModalShell title="기기 코드로 연결" onClose={() => { if (!work.pending) onClose(); }}
    footer={<>
      <Button disabled={work.pending} onClick={onClose}>취소</Button>
      <Button variant="primary" disabled={busy || !valid || !writer || !confirmed} onClick={() => void submit()}>{work.pending ? '연결 중…' : '이 그룹에 연결'}</Button>
    </>}>
    <div className="space-y-4">
      <p className="font-semibold">연결할 조직: {scope?.orgName ?? '현재 조직'}</p>
      <p className="text-[13px] text-text-dim">기기에 표시된 코드를 입력하면 현재 조직의 선택한 그룹으로 연결됩니다. 기기 인증 정보는 해당 기기에만 전달됩니다.</p>
      <div className="text-[14px]"><label className="block" htmlFor={`${ids}-code`}>기기 연결 코드</label>
        <input id={`${ids}-code`} aria-label="기기 연결 코드" maxLength={16} autoComplete="off" value={code} disabled={busy}
          onChange={event => { setCode(event.target.value.toUpperCase()); setConfirmed(false); }} className="mt-1 w-full rounded border border-line bg-panel-2 p-2 font-mono text-[20px]" /></div>
      <div className="text-[14px]"><label className="block" htmlFor={`${ids}-name`}>기기 이름</label>
        <input id={`${ids}-name`} aria-label="연결할 기기 이름" maxLength={100} value={name} disabled={busy}
          onChange={event => { setName(event.target.value); setConfirmed(false); }} className="mt-1 w-full rounded border border-line bg-panel-2 p-2" /></div>
      <div className="text-[14px]"><label className="block" htmlFor={`${ids}-group`}>담당 그룹</label>
        <select id={`${ids}-group`} aria-label="연결할 담당 그룹" value={selected} disabled={busy || !groups.length}
          onChange={event => { setGroupId(event.target.value); setConfirmed(false); }} className="mt-1 w-full rounded border border-line bg-panel-2 p-2">
          {!groups.length && <option value="">연결 가능한 담당 그룹 없음</option>}
          {groups.map(group => <option key={group.id} value={group.id}>{group.name}</option>)}
        </select></div>
      <label className="flex items-start gap-2 text-[13px]"><input type="checkbox" checked={confirmed} disabled={busy || !valid}
        onChange={event => setConfirmed(event.target.checked)} />기기 이름과 담당 그룹을 확인했으며 이 조직에 연결합니다.</label>
      {(!writer && !work.loading) && <p role="status">기기 연결에는 조직 또는 담당 그룹의 관리자 권한이 필요합니다.</p>}
      {(error || work.error) && <p role="alert" className="text-red">{error || work.error}</p>}
    </div>
  </ModalShell>;
}

export function DeviceCodeClaimButton({ onClaimed, disabled = false, contextMismatch = false, disabledReason }: {
  onClaimed: () => void; disabled?: boolean; contextMismatch?: boolean;
  /** 비활성 이유(툴팁). 버튼을 숨기지 않고 왜 못 누르는지 알려 준다 — 검수 S1. */
  disabledReason?: string | undefined;
}): JSX.Element {
  const initialCode = new URLSearchParams(window.location.hash.split('?')[1] ?? '').get('claim') ?? '';
  const [open, setOpen] = useState(!!initialCode && !disabled);
  return <><Button disabled={disabled || contextMismatch}
    title={contextMismatch ? '해당 조직의 관리 화면으로 전환한 후 코드를 연결하세요.' : disabledReason ?? '현재 조직에 기기 코드 연결'}
    onClick={() => setOpen(true)}>기기 코드로 연결</Button>
    {contextMismatch && <span className="text-[12px] text-text-dim">코드 연결은 해당 조직의 관리 화면에서 진행하세요.</span>}
    {open && !disabled && <ClaimForm initialCode={initialCode} onClose={() => setOpen(false)} onClaimed={onClaimed} />}</>;
}
